import {
  Children,
  type ComponentPropsWithoutRef,
  createContext,
  isValidElement,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
} from "react";
import { Icon, type IconComponent } from "../Icon";
import styles from "./TabGroup.module.css";

/**
 * Tab / TabGroup — segmented tab primitive.
 *
 * Matches the Figma Segment / Segment Tab component set (fileKey
 * QkVMIm6TUdblppfqYlUtai, node 1110:2951). Pill-shaped container
 * with 2–N tab buttons; the selected tab paints a raised
 * white-on-secondary surface (light) or dark-on-secondary surface
 * (dark) with a drop shadow.
 *
 * Visually the Figma node is labelled "Segment" and acts as a
 * segmented control, but the ARIA pattern (tablist / tab /
 * roving tabindex / arrow-key navigation) is the more accessible
 * choice and matches the user's terminology. Consumers using this
 * for mutually-exclusive option selection (like SettingsPanel's
 * theme / view / on-off toggles) get correct-enough semantics for
 * screen readers without a dedicated radiogroup variant.
 *
 * API (composable):
 *
 *     <TabGroup value={tab} onChange={setTab}>
 *       <Tab value="light" icon={Sun}>Light</Tab>
 *       <Tab value="dark" icon={Moon}>Dark</Tab>
 *       <Tab value="auto">Auto</Tab>
 *     </TabGroup>
 *
 * TabGroup owns the selected value + dispatches changes. Tab reads
 * both from React context (`TabGroupContext`) — consumers cannot
 * use Tab outside a TabGroup (it throws if context is missing).
 *
 * Keyboard:
 *   - Roving tabindex: only the selected tab is Tab-focusable
 *     (tabIndex={0}); the others carry tabIndex={-1} per ARIA
 *     authoring practices for tablist.
 *   - ArrowLeft / ArrowRight cycle focus + selection (automatic
 *     activation — the tab list is a lightweight segmented control,
 *     not a lazy panel navigator where manual activation would be
 *     warranted).
 *   - Home / End jump to the first / last tab.
 *
 * Disabled tabs:
 *   - Tabs with `disabled` skip arrow-key navigation (they are
 *     passed over, as if they weren't in the list).
 *   - Disabled tabs cannot be selected via click OR keyboard.
 */

// ─── Context ──────────────────────────────────────────────────

type TabGroupContextValue = {
  /** The group's `value` prop — drives visual selection state. */
  value: string;
  /**
   * The value that should carry `tabIndex={0}` for roving focus.
   * Equals `value` when `value` matches an enabled tab; otherwise
   * falls back to the first enabled tab so the group stays
   * keyboard-reachable even when `value` doesn't match anything.
   */
  focusableValue: string;
  onChange: (value: string) => void;
  registerTab: (value: string, el: HTMLButtonElement | null) => void;
  focusTabByDirection: (currentValue: string, direction: 1 | -1 | "start" | "end") => void;
  /** True when the whole group is disabled (propagated from TabGroup's disabled prop). */
  groupDisabled: boolean;
};

const TabGroupContext = createContext<TabGroupContextValue | null>(null);

// ─── TabGroup ─────────────────────────────────────────────────

export type TabGroupProps = {
  /** Currently selected tab value. */
  value: string;
  /** Fires with the newly selected value when the user picks a tab. */
  onChange: (value: string) => void;
  /** Tab children. Must be `<Tab>` elements. */
  children: ReactNode;
  /**
   * Optional accessible name for the tab list. When the parent
   * context already names the group (e.g. the SettingsPanel row
   * label), omit this and pass `aria-labelledby` via rest instead.
   */
  "aria-label"?: string;
  className?: string;
  /** When true, aria-disables the tablist + every tab button (mirrors Button/Input). */
  disabled?: boolean;
} & Omit<ComponentPropsWithoutRef<"div">, "className" | "children" | "onChange" | "role">;

export function TabGroup({
  value,
  onChange,
  children,
  className,
  disabled,
  ...rest
}: TabGroupProps) {
  const tabListId = useId();
  // Preserve insertion order of registered tabs so arrow-key nav
  // walks visual order, not alphabetical / ref-creation order.
  const tabRefs = useRef<Array<{ value: string; el: HTMLButtonElement | null }>>([]);

  const registerTab = useCallback((tabValue: string, el: HTMLButtonElement | null) => {
    const existingIdx = tabRefs.current.findIndex((t) => t.value === tabValue);
    if (el === null) {
      if (existingIdx !== -1) tabRefs.current.splice(existingIdx, 1);
      return;
    }
    if (existingIdx === -1) {
      tabRefs.current.push({ value: tabValue, el });
    } else {
      tabRefs.current[existingIdx].el = el;
    }
  }, []);

  const focusTabByDirection = useCallback(
    (currentValue: string, direction: 1 | -1 | "start" | "end") => {
      // Walk the FULL list, not just enabled, so the current-position
      // lookup works even when the currently focused tab is disabled
      // (e.g. it became disabled reactively after focus landed).
      // `enabledTabs` is only used for Home/End bookends.
      const allTabs = tabRefs.current.filter((t) => t.el !== null);
      const enabledTabs = allTabs.filter((t) => !t.el?.disabled);
      if (enabledTabs.length === 0) return;

      let target: (typeof allTabs)[number] | undefined;
      if (direction === "start") {
        target = enabledTabs[0];
      } else if (direction === "end") {
        target = enabledTabs[enabledTabs.length - 1];
      } else {
        const currentIdx = allTabs.findIndex((t) => t.value === currentValue);
        // If the current value isn't in the list at all, jump to the
        // first enabled tab — degenerate case, but keeps arrow keys
        // responsive instead of dead-ending.
        if (currentIdx === -1) {
          target = enabledTabs[0];
        } else {
          // Walk through the full list in the chosen direction,
          // skipping disabled tabs, until we find an enabled one.
          // Wrap-around — per ARIA authoring practices for tablist,
          // arrow keys loop at the ends rather than dead-ending.
          for (let step = 1; step <= allTabs.length; step++) {
            const candidateIdx = (currentIdx + direction * step + allTabs.length) % allTabs.length;
            const candidate = allTabs[candidateIdx];
            if (candidate?.el && !candidate.el.disabled) {
              target = candidate;
              break;
            }
          }
        }
      }
      if (target?.el) {
        target.el.focus();
        onChange(target.value);
      }
    },
    [onChange]
  );

  // Compute the focusable value by walking children at render time.
  // Roving tabindex requires SOME tab to carry tabIndex={0} — if
  // `value` doesn't match any enabled tab (stale state, disabled
  // selected tab), fall back to the first enabled tab so the group
  // stays keyboard-reachable. Reads child props directly since the
  // tabRefs registry only populates at commit time (too late for
  // the initial render's tabIndex decision).
  //
  // Children.toArray flattens fragments. We guard against non-Tab
  // nodes (text, divs, etc.) via isValidElement + type-check — the
  // JSDoc contract is "only Tab children" but this is defensive.
  const focusableValue = useMemo(() => {
    const flat = Children.toArray(children);
    let matched = false;
    let firstEnabled: string | undefined;
    for (const child of flat) {
      if (!isValidElement(child)) continue;
      // `child.type === Tab` is reference equality — works for direct
      // Tab imports. HOC wrappers or forwardRef re-exports would need
      // a display-name check instead.
      if (child.type !== Tab) continue;
      const props = child.props as TabProps;
      if (props.disabled) continue;
      if (firstEnabled === undefined) firstEnabled = props.value;
      if (props.value === value) {
        matched = true;
        break;
      }
    }
    return matched ? value : (firstEnabled ?? value);
  }, [children, value]);

  const contextValue = useMemo<TabGroupContextValue>(
    () => ({
      value,
      focusableValue,
      onChange,
      registerTab,
      focusTabByDirection,
      groupDisabled: disabled ?? false,
    }),
    [value, focusableValue, onChange, registerTab, focusTabByDirection, disabled]
  );

  const classes = [styles.tabGroup, className].filter(Boolean).join(" ");

  return (
    <div
      className={classes}
      role="tablist"
      id={tabListId}
      aria-disabled={disabled || undefined}
      {...rest}
    >
      <TabGroupContext.Provider value={contextValue}>{children}</TabGroupContext.Provider>
    </div>
  );
}

// ─── Tab ──────────────────────────────────────────────────────

// Shared props between the two Tab shapes.
type TabPropsBase = {
  /** Unique value identifying this tab. Must match the TabGroup's `value` prop to render as selected. */
  value: string;
  /** Optional 14×14 leading icon before the label (labeled mode), or the ONLY child (icon-only mode). */
  icon?: IconComponent;
  disabled?: boolean;
  className?: string;
} & Omit<
  ComponentPropsWithoutRef<"button">,
  "className" | "children" | "value" | "role" | "aria-selected" | "onClick" | "type"
>;

/**
 * Labeled tab — has visible text (`children`). Icon is optional; when
 * present it sits before the label. `aria-label` is optional since
 * the visible text already names the tab.
 */
type LabeledTabProps = TabPropsBase & {
  children: ReactNode;
};

/**
 * Icon-only tab — no visible text. `icon` is required (otherwise the
 * tab renders empty), and `aria-label` is required at the type level
 * because the visible affordance can't name itself for screen readers.
 * The tab also gets a tighter square padding via the `.iconOnly` class.
 */
type IconOnlyTabProps = TabPropsBase & {
  children?: undefined;
  icon: IconComponent;
  "aria-label": string;
};

export type TabProps = LabeledTabProps | IconOnlyTabProps;

export function Tab({ value, children, icon, disabled, className, ...rest }: TabProps) {
  const ctx = useContext(TabGroupContext);
  if (!ctx) {
    throw new Error("<Tab> must be rendered inside a <TabGroup>.");
  }

  const {
    value: selectedValue,
    focusableValue,
    onChange,
    registerTab,
    focusTabByDirection,
    groupDisabled,
  } = ctx;
  const isDisabled = disabled || groupDisabled;
  const selected = value === selectedValue;
  // `selected` drives the visual state (which tab paints as active);
  // `focusable` drives roving tabindex. They're almost always equal,
  // but diverge when `value` doesn't match any enabled tab — the
  // first enabled tab becomes focusable (so the group stays
  // keyboard-reachable) without being marked selected.
  const focusable = value === focusableValue;

  const buttonRef = useCallback(
    (el: HTMLButtonElement | null) => {
      registerTab(value, el);
    },
    [registerTab, value]
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        focusTabByDirection(value, 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        focusTabByDirection(value, -1);
        break;
      case "Home":
        event.preventDefault();
        focusTabByDirection(value, "start");
        break;
      case "End":
        event.preventDefault();
        focusTabByDirection(value, "end");
        break;
      default:
        break;
    }
  };

  // Icon-only mode is detected structurally (children absent) rather
  // than via a prop, so the types (discriminated union above) and the
  // runtime render path stay aligned without a redundant `iconOnly`
  // flag that callers could contradict.
  const isIconOnly = children === undefined;

  const classes = [
    styles.tab,
    selected ? styles.selected : null,
    isIconOnly ? styles.iconOnly : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      ref={buttonRef}
      type="button"
      role="tab"
      aria-selected={selected}
      // Roving tabindex: the focusable tab (selected, or the first-
      // enabled fallback when `value` matches no enabled tab) carries
      // tabIndex={0}; the others carry tabIndex={-1}. Arrow keys move
      // focus + selection between peers (see handleKeyDown). Without
      // this fallback, a stale or disabled `value` would leave every
      // tab at tabIndex={-1} and render the whole group keyboard-
      // unreachable.
      tabIndex={focusable ? 0 : -1}
      disabled={isDisabled}
      // Explicit disabled guard. Native <button disabled> already
      // suppresses real user clicks, but synthetic clicks (programmatic
      // .click(), some testing libraries) fire the onClick anyway —
      // guarding here keeps the documented contract "Disabled tabs
      // cannot be selected via click OR keyboard" true end-to-end.
      onClick={() => {
        if (isDisabled) return;
        onChange(value);
      }}
      onKeyDown={handleKeyDown}
      className={classes}
      {...rest}
    >
      {icon && <Icon as={icon} size="xs" tone="inherit" />}
      {!isIconOnly && <span className={styles.label}>{children}</span>}
    </button>
  );
}
